import arrayMutators from 'final-form-arrays';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';

import { gengql } from '@/__generated__';
import { EngagementType } from '@/__generated__/graphql';
import { notify } from '@/actions/ui';
import { ExpertHeader } from '@/auth/components/NewCustomerSignupModal/components';
import { getApiToken } from '@/auth/utils/cognitoSession';
import Dialog from '@/components/Dialog';
import { sharedRefusalCopy } from '@/components/RequestConsultation/constants';
import { SchedulingForm } from '@/components/SchedulingForm';
import config from '@/config';
import {
  MIN_HOURS_NOTICE_WITHOUT_ER,
  getSuggestedTimes,
  validateDatesMinNotice,
} from '@/consultation';
import { ERROR_CODES, GraphQLClient, apiErrorMessage, hasErrorCode } from '@/core/api';
import { UNREGISTERED_EXPERT_REQUEST_MESSAGE } from '@/core/apiErrorMessages';
import { useAppDispatch, useAppSelector } from '@/store';

import { PaymentMethodDisplay } from './PaymentMethodDisplay';
import { bookingFeeGate } from './bookingFeeGate';
import { useStyles } from './styles';

// Same mutation as NewCustomerSignupModal - no credits parameter for SMB
const REQUEST_CONSULTATION_MUTATION = gengql(/* GraphQL */ `
  mutation smbRequestConsultation(
    $expert_id: String!
    $dates: [Datetime!]
    $duration: Duration
    $description: String
    $engagement_type: EngagementType!
    $group_id: String
    $expert_request_id: String
  ) {
    requestConsultation(
      expert_id: $expert_id
      proposed_times: $dates
      expected_duration: $duration
      description: $description
      engagement_type: $engagement_type
      group_id: $group_id
      expert_request_id: $expert_request_id
    ) {
      id
      html_url
    }
  }
`);

// Pre-confirm disclosure of the ER-originated $350 booking fee. Sourced from the
// backend (site_settings, admin-editable) so the displayed fee can never drift
// from what's charged.
const BOOKING_FEE_QUERY = gengql(/* GraphQL */ `
  query smbBookingFee(
    $expert_id: String!
    $expert_request_id: String
    $group_id: String
    $engagement_type: EngagementType!
  ) {
    bookingFee(
      expert_id: $expert_id
      expert_request_id: $expert_request_id
      group_id: $group_id
      engagement_type: $engagement_type
    ) {
      cents
      currency
    }
  }
`);

export interface SMBConsultationRequestProps {
  open: boolean;
  onClose: () => void;
  expert: {
    id: string;
    name: string;
    slug?: string;
    title?: string;
    pictureUrl?: string;
    timezone?: string;
    // Mirrors PROFILE_FIELDS (src/profile/store/queries.ts) — client_rate is nullable in the
    // schema, so this must be too. `tsc` cannot enforce the correspondence: every call site
    // assembles this object from a `profile: any`, which is how the mirror drifted into
    // claiming non-null in the first place (ENG-5571).
    clientRate?: number | null; // Client-facing rate, take rate applied, in cents
  };
  // Optional billing account passed from parent (when viewer context is available)
  billingAccount?: {
    id: string;
    stripe_customer_id?: string;
    isSMBAccount?: boolean;
  };
  // Team/group ID for consultation association (ensures consultation appears in team context)
  groupId?: string;
  // Expert request this consultation originates from (drives the $350 booking fee + disclosure)
  expertRequest?: { id: string };
}

interface FormValues {
  dates: string[];
  duration: string;
  description: string;
}

/**
 * SMB-specific consultation request component.
 *
 * Uses dollar-based pricing (not credits) and displays the user's saved payment method.
 * This component is only rendered for authenticated SMB customers with Stripe billing.
 *
 * Flow:
 * 1. User fills out scheduling form (dates, duration, description)
 * 2. Shows payment method and calculated price; when launched from an expert
 *    request, also discloses the $350 booking fee folded into the charge
 * 3. On submit, creates the consultation request. Billing is ER-originated:
 *    the backend charges the $350 booking fee only when an expert_request_id
 *    is present (folded into the Stripe hold, released on cancel).
 */
export function SMBConsultationRequest({
  open,
  onClose,
  expert,
  billingAccount,
  groupId,
  expertRequest,
}: SMBConsultationRequestProps): JSX.Element {
  const s = useStyles();
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingFee, setBookingFee] = useState<{ cents: string; currency: string } | null>(null);
  const [bookingFeeError, setBookingFeeError] = useState(false);
  const [bookingFeeReload, setBookingFeeReload] = useState(0);
  const isAdmin = useAppSelector((state) => state.viewer.admin);

  // Get viewer's timezone
  const viewerTimezone = useMemo(() => moment.tz.guess(), []);

  // Viewer object for SchedulingForm
  const viewerObj = useMemo(
    () => ({
      timezone: viewerTimezone,
    }),
    [viewerTimezone]
  );

  // Initial form values with suggested times
  const initialValues = useMemo(
    () => ({
      dates: getSuggestedTimes({
        t1: viewerTimezone,
        t2: expert.timezone || viewerTimezone,
        hourOffset: MIN_HOURS_NOTICE_WITHOUT_ER,
        hourInterval: 1,
        skipWeekends: true,
      }),
      duration: '1h',
      description: '',
    }),
    [viewerTimezone, expert.timezone]
  );

  // Pricing config for dollar display (clientRate already has the take rate applied).
  //
  // Never substitute billRate: that is the expert's raw rate BEFORE the platform take
  // rate, so quoting it understates the price the customer will actually be charged
  // (a $200 quote against a $250 charge at the 20% SMB rate) and discloses the
  // expert's take-home. The old `?? billRate ?? 20000` chain did both. A null rate
  // means the API could not price this expert, so say so and block the booking
  // instead — the server refuses these bookings anyway (ENG-5571).
  const pricing = useMemo(
    () => ({
      type: 'dollars' as const,
      hourlyRate: expert.clientRate ?? null,
    }),
    [expert.clientRate]
  );
  const rateUnavailable = pricing.hourlyRate === null;

  // Fetch the ER-originated booking fee for pre-confirm disclosure. Uses the same
  // per-call bearer-auth client as the mutation below (the global Apollo Client's
  // auth headers go stale mid-session), so useQuery is intentionally not used.
  // A failure here must NOT silently drop the fee line: the backend still charges
  // the fee on confirmation, so we surface an error and block submit until the real
  // fetched fee is shown (see bookingFeeGate). bookingFeeReload drives retries.
  useEffect(() => {
    if (!open || !expertRequest?.id) {
      setBookingFee(null);
      setBookingFeeError(false);
      return;
    }
    setBookingFeeError(false);

    let cancelled = false;
    getApiToken()
      .then((token) => {
        if (!token) throw new Error('Not authenticated');
        // No explicit auth: the client's authLink reads the token per request and
        // keeps cognitoRetryLink, so a Cognito expiry refreshes and retries.
        const authClient = new GraphQLClient(config.apiUrl);
        return authClient.client.query({
          query: BOOKING_FEE_QUERY,
          variables: {
            expert_id: expert.id,
            expert_request_id: expertRequest.id,
            group_id: groupId,
            engagement_type: EngagementType.Consultation,
          },
        });
      })
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.bookingFee) {
          setBookingFee(data.bookingFee);
        } else {
          setBookingFeeError(true);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Failed to fetch booking fee:', error);
          setBookingFeeError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, expertRequest?.id, expert.id, groupId, bookingFeeReload]);

  const handleRetryBookingFee = () => {
    setBookingFeeError(false);
    setBookingFeeReload((n) => n + 1);
  };

  const handleSubmit = async (values: FormValues) => {
    // The submit button is disabled in this state, but the invariant shouldn't depend on that
    // condition staying correct — an unknown price must never reach a booking (ENG-5571).
    if (rateUnavailable) return;

    setIsSubmitting(true);
    try {
      const dates = values.dates.filter(Boolean);

      // Get auth token from cache
      // Note: We can't use useMutation here because the global Apollo Client
      // doesn't update its auth headers when setCache is called mid-session
      const token = await getApiToken();
      if (!token) {
        throw new Error('Not authenticated. Please log in and try again.');
      }

      // No explicit auth: the client's authLink reads the token per request and
      // keeps cognitoRetryLink, so a Cognito expiry refreshes and retries.
      const authClient = new GraphQLClient(config.apiUrl);

      // Same approach as NewCustomerSignupModal - no credits parameter for SMB.
      // Billing is dollar-based; the backend charges the $350 booking fee only
      // for ER-originated consultations (expert_request_id set).
      const { data } = await authClient.client.mutate({
        mutation: REQUEST_CONSULTATION_MUTATION,
        variables: {
          expert_id: expert.id,
          dates,
          duration: values.duration,
          description: values.description,
          engagement_type: EngagementType.Consultation,
          group_id: groupId,
          expert_request_id: expertRequest?.id,
        },
      });

      const consultation = data?.requestConsultation;
      if (consultation) {
        // Use hard navigation to specific consultation page
        // Don't call onClose() - it would reload the profile page and override navigation
        // Route middleware handles email verification redirect if needed
        window.location.href = `/consultation/${consultation.id}`;
        return;
      }
      onClose();
    } catch (error: any) {
      console.error('Failed to create consultation request:', error);
      if (hasErrorCode(error, ERROR_CODES.CONSULTATION_UNREGISTERED_EXPERT_REQUEST)) {
        // Not a failure to retry: the api has already emailed the expert, and every
        // retry emails them again.
        dispatch(notify(UNREGISTERED_EXPERT_REQUEST_MESSAGE, 'warning'));
        onClose();
        return;
      }
      dispatch(
        notify(
          sharedRefusalCopy(error) ??
            apiErrorMessage(error, 'An error occurred when trying to submit your request.'),
          'error'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateForm = (values: Partial<FormValues>) => {
    const errors: Partial<Record<keyof FormValues, string | Array<string | undefined>>> = {};

    if (!values.description?.trim()) {
      errors.description = 'Required';
    } else if (values.description.trim().length < 20) {
      errors.description = 'Please include at least 20 characters';
    }

    // Defense-in-depth for admin impersonation: admins are never classified as
    // SMB in production, so this guard is normally unreachable. Kept so staff
    // viewing the form via impersonation aren't blocked by the 48h rule.
    if (!isAdmin && values.dates) {
      const dateErrors = validateDatesMinNotice(values.dates, MIN_HOURS_NOTICE_WITHOUT_ER);
      if (dateErrors.some(Boolean)) {
        errors.dates = dateErrors;
      }
    }

    return errors;
  };

  const { submitBlocked: bookingFeeBlocksSubmit, showRetry: showBookingFeeError } = bookingFeeGate({
    hasExpertRequest: !!expertRequest?.id,
    // A resolved fee is a non-null object even at $0 (already-engaged expert), so
    // gate on whether it was fetched — never on the amount, which $0 would falsify.
    bookingFeeFetched: bookingFee !== null,
    bookingFeeError,
  });

  return (
    <Dialog classes={{ paper: s.paper }} open={open} onClose={onClose} width={640} scroll="body">
      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validateForm}
        mutators={{ ...arrayMutators }}
      >
        {({ handleSubmit: formHandleSubmit, valid }) => (
          <form onSubmit={formHandleSubmit}>
            {/* Expert header */}
            <ExpertHeader expert={{ ...expert, slug: expert.slug || '' }} />

            {/* Scheduling form with dollar pricing */}
            <div className={s.content}>
              <SchedulingForm
                recipientName={expert.name}
                recipientTimezone={expert.timezone}
                viewer={viewerObj}
                pricing={pricing}
                bookingFeeCents={bookingFee ? Number(bookingFee.cents) : undefined}
                descriptionLabel="What would you like to discuss?"
                descriptionMinLength={100}
                showTimezoneLink={false}
                showLegalNotes={true}
                submitButtonLabel="Request Call"
              />

              {rateUnavailable && (
                <div className={s.bookingFeeNotice}>
                  <p className={s.bookingFeeNoticeText}>
                    We can&apos;t show a price for this expert right now, so this request can&apos;t
                    be submitted. Please contact support.
                  </p>
                </div>
              )}

              {showBookingFeeError && (
                <div className={s.bookingFeeNotice}>
                  <p className={s.bookingFeeNoticeText}>
                    A booking fee applies to this request but could not be loaded. Please retry
                    before submitting.
                  </p>
                  <button
                    type="button"
                    className={s.changePaymentLink}
                    onClick={handleRetryBookingFee}
                  >
                    Retry
                  </button>
                </div>
              )}

              <div className={s.paymentSection}>
                <h3 className={s.sectionTitle}>Payment Method</h3>
                <PaymentMethodDisplay billingAccount={billingAccount} />
              </div>
            </div>

            {/* Footer with actions */}
            <div className={s.footer}>
              <button type="button" className={s.cancelButton} onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className={s.submitButton}
                disabled={!valid || isSubmitting || bookingFeeBlocksSubmit || rateUnavailable}
              >
                {isSubmitting ? 'Submitting...' : 'Request Call'}
              </button>
            </div>
          </form>
        )}
      </Form>
    </Dialog>
  );
}

export default SMBConsultationRequest;
